import fallbackImage from "@assets/images/fallback-game.svg";
import {IconHeart, IconHeartFilled, IconPlayerPlay} from "@tabler/icons-react";
import {useCallback, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {Carousel} from "@/components/Carousel";
import {HeadingCarousel} from "@/components/heading-carousel";
import {ImageWithLoadSpinner} from "@/components/image-with-load-spinner";
import useAddFavoriteGame from "@/hooks/games/useAddFavoriteGame.hook";
import useDeleteFavoriteGame from "@/hooks/games/useDeleteFavoriteGame.hook";
import useListFavoriteGames from "@/hooks/games/useListFavoriteGame.hook";
import useGetUserInfo from "@/hooks/useGetUserInfo";
import {useAuthStore} from "@/store/useAuth.store";
import {useGameMetadataStore} from "@/store/useGameMetadataStore";
import {useMiniGames} from "@/store/useMiniGames";
import styles from "./favorite-games-carousel.styles.module.css";

const FAVORITE_CAROUSEL_SKELETON_KEYS = [
  "fav-carousel-skel-0",
  "fav-carousel-skel-1",
  "fav-carousel-skel-2",
  "fav-carousel-skel-3",
  "fav-carousel-skel-4",
  "fav-carousel-skel-5",
];

export const FavoriteGamesCarousel = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const [carouselApi, setCarouselApi] = useState(null);

  const authStatus = useAuthStore(state => state.authStatus);
  const {data: userData} = useGetUserInfo();
  const {listFavGames, isLoading} = useListFavoriteGames();
  const storeFavoriteGames = useMiniGames(state => state.favoriteGames);
  const setFavoriteGames = useMiniGames(state => state.setFavoriteGames);
  const getGameById = useGameMetadataStore(state => state.getGameById);
  const {addFavoriteGame} = useAddFavoriteGame();
  const {deleteFavoriteGame} = useDeleteFavoriteGame();

  const alias = userData?.alias || userData?.given_name || "";

  const favoriteGames = useMemo(() => {
    const apiList = listFavGames?.data?.data ?? [];
    const favIds = storeFavoriteGames.length > 0 ? storeFavoriteGames : apiList;
    if (favIds.length === 0) return [];
    return favIds.slice(0, 10).map(fav => {
      const apiFav = apiList.find(a => a.game_id === fav.game_id);
      const metaGame = getGameById(fav.game_id, fav.game_provider);
      return {
        ...apiFav,
        ...fav,
        game_provider: fav.game_provider || apiFav?.game_provider || "",
        icon: metaGame?.icon || fav.icon || apiFav?.icon || fallbackImage,
        name: metaGame?.name || fav.name || apiFav?.game_name || fav.game_name || fav.game_id,
      };
    });
  }, [storeFavoriteGames, listFavGames, getGameById]);

  const isGameFavorited = useCallback(
    gameId => storeFavoriteGames.some(f => f.game_id === gameId),
    [storeFavoriteGames]
  );

  const handleFavorite = useCallback(
    (e, game) => {
      e.stopPropagation();
      setFavoriteGames(
        {id: game.game_id, provider: game.game_provider, game_provider: game.game_provider},
        addFavoriteGame,
        deleteFavoriteGame
      );
    },
    [setFavoriteGames, addFavoriteGame, deleteFavoriteGame]
  );

  const handleGameClick = useCallback(
    (provider, gameId) => {
      navigate(`/games/${provider}/${gameId}`);
    },
    [navigate]
  );

  if (authStatus !== "authenticated" || (!isLoading && favoriteGames.length === 0)) {
    return null;
  }

  return (
    <div className={styles.container}>
      <HeadingCarousel
        title={alias ? t("homeFavoritesCarouselTitle", {alias}) : t("homeFavoritesCarouselTitleGeneric")}
        subtitle={t("homeFavoritesCarouselSubTitle")}
        icon="❤️"
        viewAllLink="/favorites"
        onPrev={carouselApi?.scrollPrev}
        onNext={carouselApi?.scrollNext}
      />
      <Carousel
        onInit={setCarouselApi}
        renderItems={() =>
          isLoading
            ? FAVORITE_CAROUSEL_SKELETON_KEYS.map(skelKey => (
                <div
                  key={skelKey}
                  className={styles.gameCard}
                >
                  <div className={styles.loadingIcon} />
                </div>
              ))
            : favoriteGames.map(game => (
                <div
                  key={`fav-${game.game_id}-${game.game_provider}`}
                  className={styles.gameCard}
                  onClick={() => handleGameClick(game.game_provider, game.game_id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => e.key === "Enter" && handleGameClick(game.game_provider, game.game_id)}
                >
                  <ImageWithLoadSpinner
                    src={game.icon || fallbackImage}
                    alt={game.name}
                    fallbackSrc={fallbackImage}
                    wrapperClassName={styles.gameIcon}
                    objectFit="cover"
                    showSpinner={false}
                  />
                  <div className={styles.gradientOverlay} />
                  <div className={styles.hoverOverlay}>
                    <div className={styles.playBtn}>
                      <IconPlayerPlay
                        size={16}
                        fill="#000"
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    className={`${styles.favBtn} ${isGameFavorited(game.game_id) ? styles.favActive : ""}`}
                    onClick={e => handleFavorite(e, game)}
                    aria-label={isGameFavorited(game.game_id) ? t("favoritesRemove") : t("favoritesAdd")}
                  >
                    {isGameFavorited(game.game_id) ? (
                      <IconHeartFilled
                        size={13}
                        color="#ff4d6a"
                      />
                    ) : (
                      <IconHeart
                        size={13}
                        stroke={2}
                        color="#fff"
                      />
                    )}
                  </button>
                  <div className={styles.cardFooter}>
                    <span className={styles.gameName}>{game.name}</span>
                    <span className={styles.providerName}>{game.game_provider}</span>
                  </div>
                </div>
              ))
        }
      />
    </div>
  );
};
